import { useState } from "react";
import { IconAlertTriangle, IconCircleCheck, IconDeviceFloppy, IconShoppingCart, IconX } from "@tabler/icons-react";
import {
  Alert,
  Badge,
  Button,
  Card,
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  Group,
  Stack,
  Table,
  Tbody,
  Td,
  Text,
  Th,
  Thead,
  Tr,
} from "@/components/ui";
import { formatearMonto } from "@/shared/format/money";
import { etiquetaEstatusCompra } from "@/shared/format/estatus-compra";
import type { OrdenCompraAdmin } from "@/lib/types";
import {
  AltaCatalogoModal,
  CeldaCatalogo,
  useCatalogoVerificacion,
  type TipoCatalogo,
} from "@/features/admin-compras-shared/catalogo-verificacion";

function Campo({ label, valor }: { label: string; valor: React.ReactNode }) {
  return (
    <Group gap={6} className="flex-nowrap items-baseline">
      <Text size="xs" c="dimmed" className="min-w-22">{label}</Text>
      <Text size="sm" fw={500}>{valor}</Text>
    </Group>
  );
}

function TituloSeccion({ children }: { children: React.ReactNode }) {
  return (
    <Text size="xs" fw={700} c="multiva" className="uppercase tracking-wide">
      {children}
    </Text>
  );
}

/** Previsualización de la orden de compra parseada desde un XML, ANTES de confirmar la carga. */
export function OrdenPreviewModal({
  orden,
  confirmando,
  onConfirm,
  onCancel,
}: {
  orden: OrdenCompraAdmin | null;
  confirmando: boolean;
  onConfirm: () => void;
  onCancel: () => void;
}) {
  const { cargando, estadoCuenta, estadoCeco } = useCatalogoVerificacion();
  const [alta, setAlta] = useState<{ tipo: TipoCatalogo; codigo: string } | null>(null);

  const faltantes = (orden?.conceptos ?? []).filter(
    (c) => estadoCuenta(c.cuentaContable) === "no" || estadoCeco(c.centroCostos) === "no"
  ).length;

  return (
    <Dialog open={orden !== null} onOpenChange={(open) => !open && onCancel()}>
      <DialogContent className="max-w-4xl">
        <DialogHeader>
          <DialogTitle>
            <Group gap={8}>
              <IconShoppingCart size={18} />
              <span>Previsualización de la orden de compra a cargar</span>
              <Badge variant="light" color="advertencia" size="sm">Sin guardar</Badge>
            </Group>
          </DialogTitle>
        </DialogHeader>
        {orden && (
          <Stack gap="md">
            {!cargando && faltantes > 0 && (
              <Alert variant="error" icon={<IconAlertTriangle size={18} />} title="Catálogos incompletos">
                {faltantes} posición(es) tienen cuenta contable o centro de costos que{" "}
                <strong>no existen</strong> en los catálogos. Usa el botón verde (+) para darlos de alta
                antes de cargar.
              </Alert>
            )}
            {!cargando && faltantes === 0 && orden.conceptos.length > 0 && (
              <Alert variant="success" icon={<IconCircleCheck size={18} />} title="Catálogos verificados">
                Todas las cuentas contables y centros de costo de las posiciones existen en los catálogos.
              </Alert>
            )}

            <Card padding="md">
              <Stack gap="sm">
                <TituloSeccion>Datos de cabecera</TituloSeccion>
                <div className="grid grid-cols-1 gap-2 sm:grid-cols-2">
                  <Campo label="No. Orden" valor={<span className="font-mono">{orden.clave}</span>} />
                  <Campo label="Fecha" valor={(orden.fecha ?? "").slice(0, 10) || "—"} />
                  <Campo label="Proveedor" valor={orden.proveedor.nombre} />
                  <Campo label="RFC" valor={<span className="font-mono">{orden.proveedor.rfc}</span>} />
                  <Campo label="Moneda" valor={orden.moneda} />
                  <Campo label="Estatus" valor={etiquetaEstatusCompra(orden.estatus)} />
                </div>
              </Stack>
            </Card>

            <Card padding="md">
              <Stack gap="sm">
                <TituloSeccion>Detalle por posición ({orden.conceptos.length})</TituloSeccion>
                <div className="overflow-x-auto">
                  <Table verticalSpacing="xs" withRowBorders>
                    <Thead>
                      <Tr>
                        <Th>Línea</Th>
                        <Th>Descripción</Th>
                        <Th align="right">Cantidad</Th>
                        <Th>Unidad</Th>
                        <Th align="right">V. unitario</Th>
                        <Th align="right">Importe</Th>
                        <Th>Cuenta</Th>
                        <Th>CeCo</Th>
                      </Tr>
                    </Thead>
                    <Tbody>
                      {orden.conceptos.map((c, i) => (
                        <Tr key={i}>
                          <Td className="font-mono">{c.lineaPedido}</Td>
                          <Td>{c.descripcion}</Td>
                          <Td align="right">{c.cantidad}</Td>
                          <Td>{c.unidad ?? "—"}</Td>
                          <Td align="right">{formatearMonto(c.valorUnitario, orden.moneda)}</Td>
                          <Td align="right">{formatearMonto(c.importe, orden.moneda)}</Td>
                          <Td>
                            <CeldaCatalogo
                              valor={c.cuentaContable}
                              estado={estadoCuenta(c.cuentaContable)}
                              onAgregar={c.cuentaContable ? () => setAlta({ tipo: "cuenta", codigo: c.cuentaContable! }) : undefined}
                            />
                          </Td>
                          <Td>
                            <CeldaCatalogo
                              valor={c.centroCostos}
                              estado={estadoCeco(c.centroCostos)}
                              onAgregar={c.centroCostos ? () => setAlta({ tipo: "ceco", codigo: c.centroCostos! }) : undefined}
                            />
                          </Td>
                        </Tr>
                      ))}
                    </Tbody>
                  </Table>
                </div>
                <Text size="xs" c="dimmed">
                  Las entradas de almacén no vienen en este XML · se cargan por archivos ENTRADAS
                  aparte (servicio externo).
                </Text>
              </Stack>
            </Card>

            <Card padding="md">
              <Group justify="space-between">
                <TituloSeccion>Importe de la orden</TituloSeccion>
                <Text size="lg" fw={700}>{formatearMonto(orden.total, orden.moneda)}</Text>
              </Group>
            </Card>

            <Group justify="flex-end">
              <Button variant="default" leftSection={<IconX size={14} />} onClick={onCancel}>
                Cancelar
              </Button>
              <Button leftSection={<IconDeviceFloppy size={14} />} loading={confirmando} onClick={onConfirm}>
                Confirmar carga
              </Button>
            </Group>
          </Stack>
        )}

        <AltaCatalogoModal
          tipo={alta?.tipo ?? null}
          codigoInicial={alta?.codigo ?? ""}
          onClose={() => setAlta(null)}
        />
      </DialogContent>
    </Dialog>
  );
}
